import { useMemo, useState } from 'react';

import type { ErrorLike } from '@apollo/client';
import { useMutation } from '@apollo/client/react';
import TimelineConnector from '@mui/lab/TimelineConnector';
import TimelineContent from '@mui/lab/TimelineContent';
import TimelineItem from '@mui/lab/TimelineItem';
import TimelineSeparator from '@mui/lab/TimelineSeparator';
import { Alert, Button, Paper, Stack, Typography } from '@mui/material';
import { ContactRoundIcon } from 'lucide-react';
import { Link } from 'react-router-dom';

import { gql } from '@graphql/__generated__';
import type { ActivityFieldsFragment } from '@graphql/__generated__/graphql';
import { ProfileInquiryAssignmentTitle } from '@modules/_shared/components/feed/ProfileInquiryAssignmentTitle';
import { GET_PENDING_ACTIVITIES } from '@modules/_shared/components/feed/utils/activityQueries';
import { isProfileInquiryAssignment } from '@modules/_shared/components/feed/utils/activitySource';
import { MutationErrorSnackbar } from '@modules/_shared/components/MutationErrorModal';
import { TimeAgo } from '@modules/_shared/components/TimeAgo';
import { useTranslationWithComponents } from '@modules/_shared/hooks/useTranslationWithComponents';
import { useAppData } from '@modules/_shared/providers/AppDataProvider';
import { emitFeedRefresh } from '@modules/_shared/utils/feed-refresh';

export const CLAIM_LEAD_MUTATION = gql(/* GraphQL */ `
  mutation ClaimLead(
    $lead_id: uuid!
    $user_id: uuid!
    $should_create_call_activity: Boolean = true
  ) {
    claim_lead(
      id: $lead_id
      broker_id: $user_id
      should_create_call_activity: $should_create_call_activity
    ) {
      id
    }
  }
`);

export const PASS_LEAD_MUTATION = gql(/* GraphQL */ `
  mutation PassLead($lead_id: uuid!, $user_id: uuid!, $note: String) {
    refuse_lead(id: $lead_id, broker_id: $user_id, note: $note) {
      id
    }
  }
`);

export const Assignment = ({
  activity,
}: {
  activity: ActivityFieldsFragment;
}) => {
  const t = useTranslationWithComponents();
  const { me } = useAppData();
  const [showJSON, setShowJSON] = useState(false);
  const [error, setError] = useState<ErrorLike | null>(null);

  const [claimLead] = useMutation(CLAIM_LEAD_MUTATION, {
    onCompleted: emitFeedRefresh,
    refetchQueries: ['LeadFeed', GET_PENDING_ACTIVITIES],
    onError: setError,
  });
  const [passLead] = useMutation(PASS_LEAD_MUTATION, {
    onCompleted: emitFeedRefresh,
    refetchQueries: ['LeadFeed', 'GetLeadAgents'],
    onError: setError,
  });

  const assignmentTitle = useMemo(() => {
    if (isProfileInquiryAssignment(activity) && activity.assignee != null) {
      return (
        <Typography>
          <ProfileInquiryAssignmentTitle
            contact={
              me?.is_admin
                ? [
                    activity.lead?.contact?.first_name,
                    activity.lead?.contact?.last_name,
                  ]
                    .filter(Boolean)
                    .join(' ')
                : undefined
            }
            agent={
              <Link to={`/users/${activity.assignee.id}`}>
                <strong>{activity.assignee.full_name}</strong>
              </Link>
            }
          />
        </Typography>
      );
    }

    if (
      activity.lead?.source.name === 'appraisal' &&
      activity.created_by === activity.lead?.created_by
    ) {
      return (
        <Typography>
          {t('{{contact}} requested an on-site appraisal from {{agent}}', {
            contact: (
              <Link to={`/users/${activity.creator?.id}`}>
                <strong>{activity.creator?.full_name}</strong>
              </Link>
            ),
            agent: (
              <Link to={`/users/${activity.assignee?.id}`}>
                <strong>{activity.assignee?.full_name}</strong>
              </Link>
            ),
          })}
        </Typography>
      );
    }

    return (
      <Typography>
        <Link to={`/users/${activity.creator?.id}`}>
          <strong>{activity.creator?.full_name}</strong>
        </Link>{' '}
        {t('assigned a lead to')}{' '}
        <Link to={`/users/${activity.assignee?.id}`}>
          <strong>{activity.assignee?.full_name}</strong>
        </Link>
      </Typography>
    );
  }, [activity, me, t]);

  let assignmentIconColor = 'var(--mui-palette-primary-main)';
  if (activity.done === true && activity.success === true) {
    assignmentIconColor = 'var(--mui-palette-success-main)';
  } else if (activity.done === true && activity.success === false) {
    assignmentIconColor = 'var(--mui-palette-error-main)';
  }

  return (
    <>
      <TimelineItem key={activity?.id} style={{ fontSize: 10 }}>
        <TimelineSeparator>
          <TimelineConnector sx={{ height: '6px', flexGrow: 0 }} />
          <div onClick={() => setShowJSON(!showJSON)}>
            <ContactRoundIcon color={assignmentIconColor} size={18} />
          </div>
          <TimelineConnector />
        </TimelineSeparator>
        <TimelineContent sx={{ pr: 0 }}>
          {assignmentTitle}
          <Typography
            variant="body2"
            sx={{
              color: 'text.secondary',
              mb: 0.5,
            }}
          >
            <TimeAgo dateString={activity?.created_at} />
          </Typography>
          {activity.done === false && (
            <Stack direction="row" spacing={1}>
              <Button
                variant="contained"
                color="primary"
                size="small"
                onClick={() =>
                  claimLead({
                    variables: {
                      lead_id: activity.lead_id ?? '',
                      user_id: activity.assignee?.id ?? '',
                    },
                  })
                }
                disableElevation
              >
                {t('Claim')}
              </Button>
              <Button
                variant="outlined"
                color="error"
                size="small"
                disableElevation
                onClick={() =>
                  passLead({
                    variables: {
                      lead_id: activity.lead_id ?? '',
                      user_id: activity.assignee?.id ?? '',
                    },
                  })
                }
              >
                {t('Pass')}
              </Button>
              <Button
                variant="outlined"
                color="neutral"
                size="small"
                // TODO: add reassign logic
                disabled
                disableElevation
              >
                {t('Reassign')}
              </Button>
            </Stack>
          )}
          {activity.done === true && activity.success === true && (
            <Typography>{t('Lead accepted')}</Typography>
          )}
          {activity.done === true && activity.success === false && (
            <Typography>{t('Lead passed')}</Typography>
          )}
          {showJSON && (
            <Paper sx={{ p: 1.5 }} elevation={0} variant="outlined">
              <Alert
                severity="info"
                sx={{
                  fontSize: 12,
                  fontFamily: 'monospace',
                  position: 'relative',
                  height: 500,
                }}
              >
                <pre
                  style={{
                    overflow: 'auto',
                    position: 'absolute',
                    top: 20,
                    left: 40,
                    right: 0,
                    bottom: 0,
                  }}
                >
                  {JSON.stringify(activity, null, 2)}
                </pre>
              </Alert>
            </Paper>
          )}
        </TimelineContent>
      </TimelineItem>
      <MutationErrorSnackbar error={error} onClose={() => setError(null)} />
    </>
  );
};
